/**
 * Notification payload parsing, deduplication, and display text builders.
 */
import { NOTIFICATION_TYPES, DEDUPE_WINDOW_MS } from '../constants';
import type {
  NotificationPayload,
  NotificationMember,
  RemoteMessageLike,
  SerializedNotificationData,
  TranslateFn,
} from '../types';

const recentNotificationIds = new Map<string, number>();

export const hashNotificationId = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) % 2147483647;
  }
  return String(hash);
};

export const isDuplicateNotification = (
  id?: string | number | null,
): boolean => {
  if (!id) return false;
  const key = String(id);
  const now = Date.now();
  const seenAt = recentNotificationIds.get(key);
  if (seenAt && now - seenAt < DEDUPE_WINDOW_MS) {
    return true;
  }
  recentNotificationIds.set(key, now);
  if (recentNotificationIds.size > 50) {
    for (const [mapKey, ts] of recentNotificationIds) {
      if (now - ts > DEDUPE_WINDOW_MS) {
        recentNotificationIds.delete(mapKey);
      }
    }
  }
  return false;
};

export const parseRemoteMessage = (
  remoteMessage?: RemoteMessageLike | null,
): NotificationPayload => {
  const rawData = remoteMessage?.data || {};
  const data: Record<string, string> = {};
  Object.entries(rawData).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    data[key] = typeof value === 'string' ? value : String(value);
  });
  const notification = remoteMessage?.notification || {};

  const postId =
    data.postId ||
    data.post_id ||
    data.PostId ||
    data.postID ||
    data.feedId ||
    data.feed_id ||
    data.FeedId ||
    null;

  return {
    type: data.type || (postId ? NOTIFICATION_TYPES.FEED : NOTIFICATION_TYPES.CHAT_MESSAGE),
    conversationId: data.conversationId || data.conversation_id || null,
    category: data.category || 'buddies',
    senderId: data.senderId || data.sender_id || null,
    senderName: data.senderName || data.sender_name || notification.title || '',
    groupName: data.groupName || data.group_name || '',
    messagePreview:
      data.messagePreview || data.message_preview || notification.body || '',
    reaction: data.reaction || '',
    messageId: data.messageId || data.message_id || null,
    avatar: data.avatar || null,
    userId: data.userId || data.user_id || null,
    feedId: data.feedId || data.feed_id || postId,
    postId,
    postCommentId: data.postCommentId || data.post_comment_id || data.commentId || data.comment_id || null,
    groupId: data.groupId || data.group_id || null,
    profileUserGuid:
      data.profileUserGuid ||
      data.profile_user_guid ||
      data.userGuid ||
      data.user_guid ||
      null,
    members: ((): NotificationMember[] => {
      try {
        return data.members ? (JSON.parse(data.members) as NotificationMember[]) : [];
      } catch {
        return [];
      }
    })(),
  };
};

/**
 * Notification kinds that the list renders differently (badge, action buttons).
 * `buddyRequest` is the only kind that carries actions.
 */
export type NotificationKind =
  | 'buddyRequest'
  | 'groupJoinRequest'
  | 'groupPost'
  | 'like'
  | 'comment'
  | 'message'
  | 'follow'
  | 'general';

/** The fields GetNotifications returns that identify what a row is about. */
export interface NotificationKindInput {
  type?: string | null;
  notificationTypeName?: string | null;
  message?: string | null;
  actorUserId?: number | null;
  postId?: number | null;
  postCommentId?: number | null;
  groupId?: number | null;
  conversationId?: number | null;
  messageId?: number | null;
}

const KNOWN_KINDS: Record<string, NotificationKind> = {
  buddyrequest: 'buddyRequest',
  buddyinvite: 'buddyRequest',
  buddyinvitereceived: 'buddyRequest',
  groupjoinrequest: 'groupJoinRequest',
  grouppost: 'groupPost',
  like: 'like',
  comment: 'comment',
  message: 'message',
  follow: 'follow',
};

const flatten = (value?: string | null): string =>
  (value ?? '').toLowerCase().replace(/[^a-z]+/g, '');

/**
 * GetNotifications has no `type` field — it describes a row with
 * `notificationTypeName`, so the kind is derived from that label (and, for
 * rows that point at no content, from the message text).
 */
export const resolveNotificationKind = (
  item: NotificationKindInput,
): NotificationKind => {
  const explicit = KNOWN_KINDS[flatten(item.notificationTypeName)];
  if (explicit) return explicit;

  const label = flatten(item.notificationTypeName);
  const pointsAtContent = Boolean(
    item.postId ||
      item.postCommentId ||
      item.groupId ||
      item.conversationId ||
      item.messageId,
  );
  // A buddy request is the only row that targets a person instead of content.
  const personText = pointsAtContent ? label : `${label}${flatten(item.message)}`;
  const isResolved = /accept|approv|confirm|declin|reject/.test(personText);

  if (/buddy|friend/.test(personText) && !isResolved && /request|invit|wants/.test(personText)) {
    return 'buddyRequest';
  }
  if (/like|reaction|reacted/.test(label)) return 'like';
  if (/comment|reply|replied|mention/.test(label)) return 'comment';
  if (/grouppost|postingroup/.test(label)) return 'groupPost';
  if (/message|chat/.test(label)) return 'message';
  if (/follow/.test(label)) return 'follow';

  if (item.conversationId) return 'message';
  if (item.postCommentId) return 'comment';
  return 'general';
};

export const isChatNotification = (payload?: NotificationPayload): boolean =>
  Boolean(payload?.conversationId) ||
  payload?.type === NOTIFICATION_TYPES.ROBI_MESSAGE;

/** Chat threads that contribute to the launcher unread-message badge (excludes Robi). */
export const isUnreadMessageBadgeNotification = (
  payload?: Pick<NotificationPayload, 'conversationId' | 'type'>,
): boolean =>
  Boolean(payload?.conversationId) &&
  payload?.type !== NOTIFICATION_TYPES.ROBI_MESSAGE;

export const buildNotificationTitle = (
  payload: NotificationPayload,
  t: TranslateFn,
): string | undefined => {
  switch (payload.type) {
    case NOTIFICATION_TYPES.GROUP_MESSAGE:
      return payload.groupName || payload.senderName;
    case NOTIFICATION_TYPES.CHAT_REACTION:
      return t('chatNotifications.reactionTitle', { name: payload.senderName });
    case NOTIFICATION_TYPES.CHAT_REPLY:
      return t('chatNotifications.replyTitle', { name: payload.senderName });
    case NOTIFICATION_TYPES.ROBI_MESSAGE:
      return t('robi.quickAnswer');
    case NOTIFICATION_TYPES.FEED:
      return payload.senderName || t('notifications.defaultTitle');
    case NOTIFICATION_TYPES.PROFILE:
      return payload.senderName || t('notifications.defaultTitle');
    default:
      return payload.senderName || t('chatNotifications.newMessage');
  }
};

export const buildNotificationBody = (
  payload: NotificationPayload,
  t: TranslateFn,
): string | undefined => {
  switch (payload.type) {
    case NOTIFICATION_TYPES.CHAT_REACTION:
      return t('chatNotifications.reactionBody', {
        name: payload.senderName,
        reaction: payload.reaction || '❤️',
      });
    case NOTIFICATION_TYPES.CHAT_REPLY:
      return t('chatNotifications.replyBody', { name: payload.senderName });
    case NOTIFICATION_TYPES.GROUP_MESSAGE:
      return payload.messagePreview
        ? `${payload.senderName}: ${payload.messagePreview}`
        : payload.messagePreview;
    default:
      return payload.messagePreview || '';
  }
};

export const extractNotificationData = (
  notification?: { data?: Record<string, unknown> } | null,
): Record<string, unknown> => {
  if (!notification) return {};
  return parseNotificationData(notification.data || {});
};

export const serializeNotificationData = (
  data: Record<string, unknown> = {},
): SerializedNotificationData => {
  const serialized: SerializedNotificationData = {};
  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    if (typeof value === 'string') {
      serialized[key] = value;
      return;
    }
    if (typeof value === 'object') {
      serialized[key] = JSON.stringify(value);
      return;
    }
    serialized[key] = String(value);
  });
  return serialized;
};

export const parseNotificationData = (
  data: Record<string, unknown> = {},
): Record<string, unknown> => {
  if (!data || typeof data !== 'object') {
    return {};
  }

  const parsed: Record<string, unknown> = { ...data };

  if (parsed.members) {
    try {
      parsed.members = JSON.parse(parsed.members as string);
    } catch {
      parsed.members = [];
    }
  }

  return parsed;
};

export const resolveNotificationId = (
  payload: NotificationPayload,
  remoteMessage?: RemoteMessageLike | null,
): string =>
  payload.messageId ||
  remoteMessage?.messageId ||
  payload.conversationId ||
  `${payload.type}-${Date.now()}`;
